import type { JSX, ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import type { PortfolioIncome } from "@finance-os/shared-types/api";

import { formatMonth, usePortfolioFormat } from "../../lib/use-portfolio-format.js";
import { Money } from "../Money.js";
import {
  chartSeries,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../ui/chart.js";
import { Pl } from "./Pl.js";
import { PanelCard, type PanelQuery } from "./PanelCard.js";

const config = {
  net_eur_minor: { label: "Net dividends", color: chartSeries(1).color },
  withheld_eur_minor: { label: "Withholding tax", color: chartSeries(5).color },
} satisfies ChartConfig;

function Figure({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="text-lg font-semibold">{children}</div>
    </div>
  );
}

/** Dividends received per month, and what the window earned split into realized, unrealized and dividends. */
export function IncomePanel({ query }: { query: PanelQuery<PortfolioIncome> }): JSX.Element {
  const fmt = usePortfolioFormat();
  const data = query.data;
  const hasDividends = data !== undefined && (data.totals.gross_eur_minor !== 0 || data.totals.withheld_eur_minor !== 0);

  return (
    <PanelCard title="Income" isLoading={query.isLoading} isError={query.isError}>
      {data && (
        <>
          {hasDividends ? (
            <ChartContainer config={config} className="aspect-auto h-56 w-full">
              <BarChart data={data.months} margin={{ top: 4, left: 4, right: 4 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(m: string) => formatMonth(m)}
                />
                <YAxis width={56} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmt.compact(v)} />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(label: unknown) => (typeof label === "string" ? formatMonth(label) : null)}
                      valueFormatter={(v) => fmt.money(v)}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar dataKey="net_eur_minor" stackId="dividends" fill="var(--color-net_eur_minor)" radius={[0, 0, 4, 4]} />
                <Bar dataKey="withheld_eur_minor" stackId="dividends" fill="var(--color-withheld_eur_minor)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          ) : (
            <p className="text-muted-foreground text-sm">No dividends received in this period.</p>
          )}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-3">
            <Figure label="Dividends (net)">
              <Money amountMinor={data.split.dividends_net_eur_minor} native="EUR" />
            </Figure>
            <Figure label="Realized">
              <Pl minor={data.split.realized_eur_minor} />
            </Figure>
            <Figure label="Unrealized change">
              {data.split.unrealized_change_eur_minor === null ? (
                <span className="text-muted-foreground text-sm font-normal">needs two days of history</span>
              ) : (
                <Pl minor={data.split.unrealized_change_eur_minor} />
              )}
            </Figure>
          </div>
        </>
      )}
    </PanelCard>
  );
}
